import { onBeforeUnmount, onMounted, ref } from "vue";
import { useUserStore } from "~/stores/user";
import { isLogstoreEnabled } from "~/utils/analyticsEnv.js";

export function useLipSyncPageViewLog(pageType) {
  const route = useRoute();
  const nuxtApp = useNuxtApp();
  const userStore = useUserStore();
  const pageEnteredAt = ref(0);
  const leaveLogged = ref(false);
  let trackerPromise = null;
  let cancelPageOpenSchedule = null;

  function scheduleAfterInitialLoad(task, minDelayMs = 12_000) {
    if (!import.meta.client) return () => {};

    let cancelled = false;
    let timeoutId = 0;
    let idleId = 0;

    const runOnIdle = () => {
      if (cancelled) return;

      if ("requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(
          () => {
            if (!cancelled) task();
          },
          { timeout: 8000 },
        );
        return;
      }

      timeoutId = window.setTimeout(() => {
        if (!cancelled) task();
      }, 4500);
    };

    const scheduleAfterDelay = () => {
      if (cancelled) return;
      timeoutId = window.setTimeout(runOnIdle, minDelayMs);
    };

    if (document.readyState === "complete") {
      scheduleAfterDelay();
    } else {
      window.addEventListener("load", scheduleAfterDelay, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener("load", scheduleAfterDelay);
      if (idleId && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }

  function getTracker() {
    if (!trackerPromise) {
      trackerPromise = import("~/utils/lipsyncAnalytics.js").then(({ createLipSyncTracker }) =>
        createLipSyncTracker(nuxtApp, userStore, route),
      );
    }
    return trackerPromise;
  }

  const logLeave = () => {
    if (!pageEnteredAt.value || leaveLogged.value) return;
    leaveLogged.value = true;
    const duration = Math.max(0, Math.round((Date.now() - pageEnteredAt.value) / 1000));
    if (!isLogstoreEnabled(useRuntimeConfig())) return;
    void getTracker().then((tracker) => tracker.pageLeave(duration, pageType));
  };

  onMounted(() => {
    pageEnteredAt.value = Date.now();
    leaveLogged.value = false;
    if (isLogstoreEnabled(useRuntimeConfig())) {
      cancelPageOpenSchedule = scheduleAfterInitialLoad(() => {
        void getTracker().then((tracker) => tracker.pageOpen(pageType));
      });
    }
    window.addEventListener("pagehide", logLeave, { once: true });
  });

  onBeforeUnmount(() => {
    if (cancelPageOpenSchedule) {
      cancelPageOpenSchedule();
      cancelPageOpenSchedule = null;
    }
    window.removeEventListener("pagehide", logLeave);
    logLeave();
  });

  return {
    logPageLeave: logLeave,
    pageEnteredAt,
  };
}
